import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";

const navLinks = [
  ["/chambres-hotes", "nav.chambres"], ["/gites", "nav.gites"],
  ["/seminaires", "nav.seminaires"], ["/le-domaine", "nav.domaine"],
  ["/la-region", "nav.region"], ["/cheques-cadeaux", "nav.giftcards"],
  ["/informations-pratiques", "nav.infos"], ["/contact", "nav.contact"],
];

export default function Footer() {
  const { t } = useI18n();
  const { settings } = useSite();
  const year = new Date().getFullYear();
  const s = settings || {};

  return (
    <footer className="bg-ink-900 text-white/75" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-1">
          <img src="/footer-logo.png?v=2" alt="La Bégaudière" loading="lazy" decoding="async" className="h-24 lg:h-28 w-auto mb-5" data-testid="footer-logo" />
          <p className="font-body text-sm leading-relaxed text-white/70">{t("footer.tagline")}</p>
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-wider text-white/40 mb-3">{t("footer.labels")}</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-4">
              <img src="/tourisme-bretagne-white.png" alt="Label Tourisme Bretagne" loading="lazy" decoding="async" className="h-8 w-auto opacity-90" data-testid="footer-label-bretagne" />
              <img src="/offices-tourisme-france-white.png" alt="Offices de Tourisme de France" loading="lazy" decoding="async" className="h-16 w-auto opacity-90" data-testid="footer-label-otf" />
              <img src="/tourisme-saint-malo-baie-white.png" alt="Tourisme Saint-Malo Baie du Mont-Saint-Michel" loading="lazy" decoding="async" className="h-16 w-auto opacity-90" data-testid="footer-label-stmalo" />
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-heading text-lg text-white mb-4">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm font-body">
            <li className="flex gap-2.5"><MapPin size={17} strokeWidth={1.5} className="shrink-0 mt-0.5 text-brand-200" /><span>{s.address || "12 La Bégaudière, 35120 Mont-Dol"}</span></li>
            {s.phone && <li className="flex gap-2.5"><Phone size={17} strokeWidth={1.5} className="text-brand-100" /><a href={`tel:${s.phone}`} data-testid="footer-phone" className="hover:text-white">{s.phone}</a></li>}
            <li className="flex gap-2.5"><Phone size={17} strokeWidth={1.5} className="text-brand-100" /><a href={`tel:${(s.phone2 || "+33 7 64 31 80 35").replace(/\s/g, "")}`} data-testid="footer-phone2" className="hover:text-white">{s.phone2 || "+33 7 64 31 80 35"}</a></li>
            {s.email && <li className="flex gap-2.5"><Mail size={17} strokeWidth={1.5} className="text-brand-100" /><a href={`mailto:${s.email}`} data-testid="footer-email" className="hover:text-white">{s.email}</a></li>}
            {s.hours && <li className="flex gap-2.5"><Clock size={17} strokeWidth={1.5} className="text-brand-100" /><span>{s.hours}</span></li>}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg text-white mb-4">{t("footer.nav")}</h4>
          <ul className="space-y-2.5 text-sm font-body">
            {navLinks.map(([to, key]) => (
              <li key={to}><Link to={to} className="hover:text-white transition-colors">{t(key)}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg text-white mb-4">{t("footer.legal")}</h4>
          <ul className="space-y-2.5 text-sm font-body">
            <li><Link to="/mentions-legales" className="hover:text-white">{t("footer.mentions")}</Link></li>
            <li><Link to="/confidentialite" className="hover:text-white">{t("footer.privacy")}</Link></li>
            <li><Link to="/cgv" className="hover:text-white">{t("footer.cgv")}</Link></li>
            <li><Link to="/annulation" className="hover:text-white">{t("footer.cancellation")}</Link></li>
          </ul>
          {(s.facebook || s.instagram) && (
            <div className="flex gap-3 mt-5">
              {s.facebook && <a href={s.facebook} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors"><Facebook size={18} strokeWidth={1.5} /></a>}
              {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors"><Instagram size={18} strokeWidth={1.5} /></a>}
            </div>
          )}
        </div>
      </div>
      <div className="bg-brand text-white/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© {year} Domaine de la Bégaudière. {t("footer.rights")}.{s.siret ? ` · ${t("footer.siret")} ${s.siret}` : ""}</p>
          <Link to="/admin/login" rel="nofollow" className="hover:text-white" data-testid="footer-admin-link">Administration</Link>
        </div>
      </div>
    </footer>
  );
}
